import { useMemo, useState } from 'react'
import { API } from 'aws-amplify'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid'
import { isEmpty, isNumber } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import Bugsnag from '@bugsnag/js'

import ActivityIndicator from '../components/activity-indicator'
import Button from '../components/button'
import { useTranslation } from 'react-i18next'
import Input from '../components/input'
import { useQuery } from '../hooks/use-query'
import { EMAIL_REGEX } from '../helpers/utils'
import { inviteTalent } from '../graphql/queries'
import { notifyBugsnag, notifyDocumentUploadFailure } from '../helpers/bugsnag'
import { uploadDocuments, MAX_DOCUMENT_SIZE } from '../helpers/document-upload'
// import { sendSlackMessage } from '../helpers/slack'
import { getSpaceLink } from '../helpers/space-link'
import CopyToClipboard from '../components/invitation/copy-to-clipboard'
import { localStorage } from '../helpers/local-storage'
import ErrorBoundary from '../components/error-boundary'
import { trackEvent } from '../helpers/analytics'
import SelectField from '../components/select-field'
import PrivacyPolice from '../components/privacy-policy'
import { useMount } from '../hooks/use-mount'
import FileUpload from '../components/file-upload'
import { DE, EN, getSupportedLanguage } from '../helpers/languages'
import {
  initializeApp,
  INITIAL,
  DONE,
  ERROR_GET_CAREER,
  ERROR_NO_ID,
  ERROR_CAREER_NOT_ACTIVE,
  ERROR_GET_PARTNER,
  ERROR_SPACE_CREATED,
  TALENTPOOL,
  SELECT
} from '../helpers/invitation'

const isErrorState = (state) => {
  return [
    ERROR_NO_ID,
    ERROR_GET_CAREER,
    ERROR_CAREER_NOT_ACTIVE,
    ERROR_GET_PARTNER,
    ERROR_SPACE_CREATED
  ].includes(state)
}

// Die 5-stellige Prüfung passt nur auf deutsche Postleitzahlen. Für NL und FR
// (z. B. '1012 AB', '1000', '75001') reicht eine grobe Formatprüfung
const POSTCODE_REGEX_DE = /\d{5}/
const POSTCODE_REGEX_INTERNATIONAL =
  /^[A-Za-z0-9][A-Za-z0-9 -]{2,8}[A-Za-z0-9]$/

const getPostcodeRegex = (language) => {
  return [DE, EN].includes(language)
    ? POSTCODE_REGEX_DE
    : POSTCODE_REGEX_INTERNATIONAL
}

const FIELDS = {
  firstname: 'firstname',
  lastname: 'lastname',
  email: 'email',
  phone: 'phone',
  postcode: 'postcode',
  work_experience: 'work_experience'
}

const Invitation = () => {
  const { t, i18n } = useTranslation()
  const career_id = useQuery('career')
  const partner_id = useQuery('partner')
  const partner_user_id = useQuery('applicant')

  const [state, setState] = useState(INITIAL)
  const [context, setContext] = useState(INITIAL)
  const [career, setCareer] = useState(null)
  const [partner, setPartner] = useState(null)
  const [customLandingPageText, setCustomLandingPageText] = useState(null)
  const [space, setSpace] = useState(null)
  const [agreed, setAgreed] = useState(false)
  const [fields, setFields] = useState([])
  const [additionalFields, setAdditionalFields] = useState([])
  const [isUploadingFiles, setIsUploadingFiles] = useState(false)
  const [failedUploads, setFailedUploads] = useState([])

  const initialValues = useMemo(() => {
    const result = {
      firstname: fields.includes(FIELDS.firstname) ? '' : undefined,
      lastname: fields.includes(FIELDS.lastname) ? '' : undefined,
      email: fields.includes(FIELDS.email) ? '' : undefined,
      phone: fields.includes(FIELDS.phone) ? '' : undefined,
      postcode: fields.includes(FIELDS.postcode) ? '' : undefined,
      work_experience: fields.includes(FIELDS.work_experience) ? -1 : undefined
    }

    additionalFields.forEach(({ id, type, options, multiple }) => {
      if (type === 'text') {
        result[id] = ''
      } else if (type === 'dropdown' && multiple) {
        result[id] = []
      } else if (type === 'dropdown' && options && options.length > 0) {
        result[id] = options[0].value
      } else if (type === 'dropdown') {
        result[id] = ''
      } else if (type === 'file') {
        result[id] = []
      }
    })

    return result
  }, [fields, additionalFields])

  const validationSchema = useMemo(() => {
    const result = {
      firstname: fields.includes(FIELDS.firstname)
        ? Yup.string().required(t('invitation.validation_required'))
        : undefined,
      lastname: fields.includes(FIELDS.lastname)
        ? Yup.string().required(t('invitation.validation_required'))
        : undefined,
      email: fields.includes(FIELDS.email)
        ? Yup.string()
            .matches(EMAIL_REGEX, t('invitation.validation_email_error'))
            .required(t('invitation.validation_required'))
        : undefined,
      phone: fields.includes(FIELDS.phone)
        ? Yup.string()
            .matches(
              /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,9}$/, // eslint-disable-line
              t('invitation.validation_phone_error')
            )
            .required(t('invitation.validation_required'))
        : undefined,
      postcode: fields.includes(FIELDS.postcode)
        ? Yup.string()
            .matches(
              getPostcodeRegex(i18n.language),
              t('invitation.validation_postcode_error')
            )
            .required(t('invitation.validation_required'))
        : undefined,
      work_experience: fields.includes(FIELDS.work_experience)
        ? Yup.number().min(0).required(t('invitation.validation_required'))
        : undefined
    }

    additionalFields.forEach(({ id, type, required, multiple }) => {
      if (type === 'text') {
        result[id] = Yup.string()

        if (required) {
          result[id] = result[id].required(t('invitation.validation_required'))
        }
      } else if (type === 'dropdown') {
        if (multiple) {
          result[id] = Yup.array()

          if (required) {
            result[id] = result[id].min(1, t('invitation.validation_required'))
          }
        } else {
          result[id] = Yup.object()

          if (required) {
            result[id] = result[id].required(
              t('invitation.validation_required')
            )
          }
        }
      } else if (type === 'file') {
        result[id] = Yup.array()

        if (required) {
          result[id] = result[id].min(1, t('invitation.validation_required'))
        }
      }
    })

    return Yup.object(result)
  }, [fields, additionalFields, t, i18n.language])

  const handleFileSelect = (fieldId, files) => {
    formik.setFieldValue(fieldId, [...(formik.values[fieldId] || []), ...files])
  }

  const handleFileRemove = (fieldId, index) => {
    const currentFiles = formik.values[fieldId] || []
    formik.setFieldValue(
      fieldId,
      currentFiles.filter((_, i) => i !== index)
    )
  }

  const generateSpaceId = () => {
    return uuidv4()
  }

  const uploadFiles = async (space_id) => {
    setIsUploadingFiles(true)

    try {
      const { failed } = await uploadDocuments({
        space_id,
        fields: additionalFields,
        values: formik.values
      })

      failed.forEach(({ field, file, error }) =>
        notifyDocumentUploadFailure(error, {
          space_id,
          field_id: field.id,
          // kein Dateiname: enthaelt oft den Namen des Talents
          file_extension: file.name.split('.').pop().toLowerCase(),
          file_size: file.size,
          file_type: file.type,
          failed_count: failed.length
        })
      )

      return failed.map(({ field, file }) => ({
        label: field.label || t('invitation.no_label'),
        name: file.name
      }))
    } catch (err) {
      // Unerwarteter Fehler ausserhalb der Einzel-Uploads: der Space existiert
      // trotzdem, also Erfolgsseite zeigen und alle Dateien als offen melden
      notifyDocumentUploadFailure(err, { space_id })

      return additionalFields
        .filter(({ type }) => type === 'file')
        .flatMap((field) =>
          (formik.values[field.id] || []).map((file) => ({
            label: field.label || t('invitation.no_label'),
            name: file.name
          }))
        )
    } finally {
      setIsUploadingFiles(false)
    }
  }

  // Funktion zum Sammeln der fehlenden Pflichtfelder
  const getMissingRequiredFields = () => {
    const missingFields = []

    // Standard-Felder prüfen
    const fieldLabels = {
      firstname: t('invitation.firstname_label'),
      lastname: t('invitation.lastname_label'),
      email: t('invitation.email_label'),
      phone: t('invitation.phone_label'),
      postcode: t('invitation.postcode_label'),
      work_experience: t(
        `invitation.work_experience.${career_id ? 'career' : 'partner'}_label`
      )
    }

    Object.keys(fieldLabels).forEach((fieldId) => {
      if (fields.includes(fieldId) && formik.errors[fieldId]) {
        missingFields.push(fieldLabels[fieldId])
      }
    })

    // Zusätzliche Felder prüfen
    additionalFields.forEach(({ id, label, required }) => {
      if (required && formik.errors[id]) {
        missingFields.push(label || t('invitation.no_label'))
      }
    })

    // Datenschutzerklärung prüfen
    if (!agreed) {
      missingFields.push(t('invitation.privacy_policy_missing'))
    }

    return missingFields
  }

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const { firstname, lastname, email, phone, postcode, work_experience } =
        values

      try {
        const { location } = window

        let additional_requested_data = {}

        if (isNumber(work_experience) && work_experience !== -1) {
          additional_requested_data.work_experience = work_experience
        }

        additionalFields.forEach(({ id, label, type, multiple }) => {
          additional_requested_data[id] = {
            id,
            label,
            input: values[id],
            type,
            multiple: multiple || false
          }
        })

        additional_requested_data = !isEmpty(additional_requested_data)
          ? JSON.stringify(additional_requested_data)
          : undefined

        // Generate a unique Space ID
        const spaceId = generateSpaceId()

        const variables = {
          send_mail: false,
          career_id: career ? career.id : undefined,
          partner_id: partner.id,
          email: email && email.toLowerCase(),
          firstname,
          lastname,
          phone,
          postcode,
          additional_requested_data,
          partner_user_id,
          ref_link: location.href,
          referrer_link: document.referrer,
          id: spaceId
        }

        const {
          data: { inviteTalent: s }
        } = await API.graphql({
          query: inviteTalent,
          authMode: 'AWS_IAM',
          variables
        })

        // Upload files after space creation. Immer in die zurueckgegebene
        // Space-ID: bei bekannter partner_user_id liefert inviteTalent einen
        // bestehenden Space mit anderer ID als der hier erzeugten.
        Bugsnag.leaveBreadcrumb('Space created, uploading files', {
          space_id: s.id,
          requested_space_id: spaceId,
          context
        })
        setFailedUploads(await uploadFiles(s.id))

        // s.language kommt aus Space.language (Sprache der Stelle) und landet
        // als &language= im Link, damit die Talent-App darin startet
        setSpace(s)

        const i = JSON.parse(localStorage.getItem('invitations') || '{}')
        if (context === SELECT && !partner_user_id) i[career_id] = s.id
        if (context === TALENTPOOL && !partner_user_id) i[partner_id] = s.id

        localStorage.setItem('space-id', s.id)
        localStorage.setItem('invitations', JSON.stringify(i))

        trackEvent('TALENT_INVITED', { partner: partner.id })

        // Weiterleitung zum Space
        // window.location.href = getSpaceLink(s)
      } catch (err) {
        notifyBugsnag(err, 'inviteTalent') || setState(ERROR_SPACE_CREATED)
      }
    }
  })

  useMount(() => {
    const doAsync = async () => {
      const response = await initializeApp({ career_id, partner_id })
      const { context: x, error_state, career: c, partner: p } = response

      if (error_state) {
        setState(error_state)
        return
      }

      if (c) {
        setCareer(c)
        localStorage.setItem('career-id', c.id)
        // unbekannte Sprache der Stelle landet auf Englisch statt auf rohen Keys
        c.language && i18n.changeLanguage(getSupportedLanguage(c.language))
      }

      setPartner(p)
      localStorage.setItem('partner-id', p.id)
      localStorage.setItem('partner-name', p.name)

      setContext(x)

      Bugsnag.leaveBreadcrumb('Invitation loaded', {
        career_id,
        partner_id,
        context: x
      })

      if (partner_user_id) {
        localStorage.setItem('partner-user-id', partner_user_id)
        Bugsnag.setUser(partner_user_id, undefined, p.name)
      }

      const i = JSON.parse(localStorage.getItem('invitations') || '{}')

      if (x === SELECT && !partner_user_id && i[career_id]) {
        // Sprache der Stelle mitgeben, sonst startet der Link immer auf Deutsch
        setSpace({ id: i[career_id], language: c.language })
      }

      if (x === TALENTPOOL && !partner_user_id && i[partner_id]) {
        setSpace({ id: i[partner_id] })
      }

      const partnerComponents = JSON.parse(p.app_settings?.components || '{}')

      let careerComponents = {}
      let application_input_additional_fields = []

      // Lade zusätzliche Felder basierend auf dem Kontext
      if (x === SELECT) {
        careerComponents = JSON.parse(c.app_settings?.components || '{}')
        application_input_additional_fields = JSON.parse(
          c.app_settings?.application_input_additional_fields || '[]'
        )
      } else if (x === TALENTPOOL) {
        // Lade zusätzliche Felder aus dem Partner-Objekt für den Talentpool-Kontext
        const partnerAdditionalFields = JSON.parse(
          p.app_settings?.application_input_additional_fields || '[]'
        )
        application_input_additional_fields = partnerAdditionalFields
      }

      setCustomLandingPageText(
        careerComponents.customLandingPageText ||
          partnerComponents.customLandingPageText
      )

      setFields(
        careerComponents.customLandingPageFields ||
          partnerComponents.customLandingPageFields ||
          Object.keys(FIELDS)
      )

      setAdditionalFields(application_input_additional_fields)
      setState(DONE)
    }

    if (career_id || partner_id) doAsync()
    else setState(ERROR_NO_ID)
  })

  if (state === INITIAL) {
    return (
      <div className='flex justify-center pt-64'>
        <ActivityIndicator />
      </div>
    )
  }

  if (isErrorState(state)) {
    return (
      <ErrorBoundary
        error={state}
        withContactSupport={state === ERROR_SPACE_CREATED}
      />
    )
  }

  return (
    <>
      <img
        className='mx-auto w-32 h-32 object-contain'
        src={`${
          career?.logo || partner.logo
        }?random_number=${new Date().getTime()}`}
        alt=''
      />
      <div className='mt-4 text-center'>
        {context === SELECT && (
          <>
            <h2 className='text-2xl font-bold text-gray-900'>{career.title}</h2>
            <p className='text-lg text-gray-500 italic'>
              {partner.display_name || partner.name}
            </p>
          </>
        )}
        {context === TALENTPOOL && (
          <h2 className='text-2xl font-bold text-gray-900'>
            {partner.display_name || partner.name}
          </h2>
        )}
      </div>

      {!space && (
        <div className='mt-4'>
          {(customLandingPageText || '').split('\n').map((paragraph, index) => (
            <p key={index} className='mb-4 text-md text-gray-700 text-center'>
              {paragraph}
            </p>
          ))}
          <form
            onSubmit={formik.handleSubmit}
            className='mt-8 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8'
          >
            {[
              [
                'firstname',
                t('invitation.firstname_label'),
                t('invitation.firstname_placeholder'),
                '',
                true
              ],
              [
                'lastname',
                t('invitation.lastname_label'),
                t('invitation.lastname_placeholder'),
                '',
                true
              ],
              [
                'email',
                t('invitation.email_label'),
                t('invitation.email_placeholder'),
                'sm:col-span-2',
                true
              ],
              [
                'postcode',
                t('invitation.postcode_label'),
                t('invitation.postcode_placeholder'),
                '',
                false
              ],
              [
                'phone',
                t('invitation.phone_label'),
                t('invitation.phone_placeholder'),
                'sm:col-span-2',
                true
              ]
            ]
              .filter(([id]) => fields.includes(id))
              .map((input, index) => (
                <div key={index} className={input[3]}>
                  <Input
                    type='text'
                    id={input[0]}
                    label={input[1]}
                    placeholder={input[2]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values[input[0]] || ''}
                    touched={formik.touched[input[0]]}
                    error={formik.errors[input[0]]}
                    disabled={formik.isSubmitting}
                    inlineErrorMessage={input[4]}
                    required={fields.includes(input[0])}
                  />
                </div>
              ))}
            {fields.includes(FIELDS.work_experience) && (
              <div className='sm:col-span-2'>
                <SelectField
                  id='work_experience'
                  label={t(
                    `invitation.work_experience.${
                      career_id ? 'career' : 'partner'
                    }_label`
                  )}
                  options={[
                    { id: '-1', value: t('invitation.work_experience.select') },
                    { id: '0', value: t('invitation.work_experience.starter') },
                    { id: '1', value: t('invitation.work_experience.junior') },
                    {
                      id: '2',
                      value: t('invitation.work_experience.mid_level')
                    },
                    { id: '3', value: t('invitation.work_experience.senior') }
                  ]}
                  onChange={(value) =>
                    formik.setFieldValue(
                      'work_experience',
                      value ? parseInt(value) : -1
                    )
                  }
                  defaultValue={
                    formik.values.work_experience?.toString() || '-1'
                  }
                  isMultiple={false}
                  required={fields.includes(FIELDS.work_experience)}
                />
              </div>
            )}
            {additionalFields.map(
              (
                {
                  id,
                  label,
                  placeholder,
                  type,
                  options,
                  required,
                  multiple,
                  description
                },
                index
              ) => (
                <div key={index} className='sm:col-span-2'>
                  {type === 'text' ? (
                    <Input
                      type='text'
                      id={id}
                      label={label || t('invitation.no_label')}
                      placeholder={placeholder}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values[id] || ''}
                      touched={formik.touched[id]}
                      error={formik.errors[id]}
                      disabled={formik.isSubmitting}
                      description={description}
                      required={required}
                    />
                  ) : type === 'dropdown' ? (
                    <SelectField
                      id={id}
                      label={label || t('invitation.no_label')}
                      options={options}
                      onChange={(value) => {
                        if (multiple) {
                          const selectedOptions = value.map((selectedId) => {
                            const option = options.find(
                              (opt) => opt.id === selectedId
                            )
                            return {
                              id: selectedId,
                              value: option ? option.value : ''
                            }
                          })
                          formik.setFieldValue(id, selectedOptions)
                        } else {
                          const selectedOption = options.find(
                            (opt) => opt.id === value
                          )
                          formik.setFieldValue(
                            id,
                            selectedOption
                              ? {
                                  id: value,
                                  value: selectedOption.value
                                }
                              : null
                          )
                        }
                      }}
                      defaultValue={
                        multiple
                          ? (formik.values[id] || []).map((v) => v.id)
                          : formik.values[id]?.id || ''
                      }
                      isMultiple={multiple}
                      description={description}
                      required={required}
                    />
                  ) : type === 'file' ? (
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        {label || t('invitation.no_label')}
                        {required && (
                          <>
                            <span className='text-red-500 ml-1'>*</span>
                            <span className='text-gray-500 ml-1 text-xs'>
                              {t('required_field')}
                            </span>
                          </>
                        )}
                      </label>
                      <FileUpload
                        onFileSelect={(files) => handleFileSelect(id, files)}
                        onFileRemove={(index) => handleFileRemove(id, index)}
                        acceptedFileTypes='.pdf,.doc,.docx'
                        maxFileSize={MAX_DOCUMENT_SIZE}
                        multiple={multiple}
                        files={formik.values[id] || []}
                        showFileList={true}
                        description={description}
                      />
                      {formik.touched[id] && formik.errors[id] && (
                        <p className='mt-2 text-sm text-red-600'>
                          {formik.errors[id]}
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
              )
            )}
            <div className='sm:col-span-2 mt-4'>
              <PrivacyPolice {...{ partner, agreed, setAgreed }} />
            </div>
            <div className='mt-8 sm:col-span-2'>
              <div className='flex justify-end'>
                <Button.PrimaryXL
                  text={t('invitation.submit_action')}
                  disabled={!(formik.isValid && agreed) || isUploadingFiles}
                  onClick={formik.handleSubmit}
                  isLoading={formik.isSubmitting || isUploadingFiles}
                />
              </div>
              {!(formik.isValid && agreed) &&
                !isUploadingFiles &&
                !formik.isSubmitting && (
                  <div className='mt-2 flex justify-end'>
                    <div className='relative group'>
                      <p className='text-sm text-red-600 cursor-help'>
                        {t('invitation.required_fields_missing')}
                      </p>
                      <div className='absolute bottom-full right-0 mb-2 hidden group-hover:block z-10'>
                        <div className='bg-gray-900 text-white text-xs rounded py-2 px-3 shadow-lg min-w-max'>
                          <div className='font-medium mb-1'>
                            {t('invitation.missing_fields_tooltip').replace(
                              ' {{fields}}',
                              ':'
                            )}
                          </div>
                          <ul className='space-y-1'>
                            {getMissingRequiredFields().map((field, index) => (
                              <li key={index} className='flex items-center'>
                                <span className='w-1 h-1 bg-white rounded-full mr-2 flex-shrink-0'></span>
                                {field}
                              </li>
                            ))}
                          </ul>
                          <div className='absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900'></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </form>
        </div>
      )}

      {space && (
        <>
          <div className='mt-8 mx-auto max-w-sm'>
            <div className='mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100'>
              <CheckIcon
                className='h-12 w-12 text-green-600'
                aria-hidden='true'
              />
            </div>
            <div className='mt-8 text-center'>
              <h3 className='text-lg leading-6 font-medium text-gray-900'>
                {t('invitation.space_created_success_title')}
              </h3>
              <div className='mt-2'>
                <p className='text-sm text-gray-700'>
                  {t('invitation.space_created_success_description')}
                </p>
              </div>
              {failedUploads.length > 0 && (
                <div className='mt-6 rounded-md bg-yellow-50 p-4 text-left'>
                  <div className='flex'>
                    <ExclamationTriangleIcon
                      className='h-5 w-5 flex-shrink-0 text-yellow-400'
                      aria-hidden='true'
                    />
                    <div className='ml-3'>
                      <h4 className='text-sm font-medium text-yellow-800'>
                        {t('invitation.documents_upload_failed_title', {
                          count: failedUploads.length
                        })}
                      </h4>
                      <ul className='mt-2 list-disc pl-5 text-sm text-yellow-700'>
                        {failedUploads.map(({ label, name }, index) => (
                          <li key={index} className='break-words'>
                            {name} ({label})
                          </li>
                        ))}
                      </ul>
                      <p className='mt-2 text-sm text-yellow-700'>
                        {t('invitation.documents_upload_failed_description')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className='mt-12 flex flex-col'>
                <div>
                  <span className='block text-sm italic text-gray-700'>
                    {t('invitation.redirect_info')}
                  </span>
                  <span className='block mt-2 text-sm text-gray-700'>
                    {t('invitation.manually_redirect_1')}
                    <a
                      id='anchor-to-aivy'
                      href={getSpaceLink(space)}
                      target='_blank'
                      rel='noreferrer'
                      className='text-indigo-600 underline hover:text-indigo-700'
                    >
                      {t('invitation.manually_redirect_2')}
                    </a>
                    {t('invitation.manually_redirect_3')}
                  </span>
                </div>
                <div className='mt-4 flex justify-center gap-x-2'>
                  <CopyToClipboard space={space} />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default Invitation
